import { makeAutoObservable } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { EnumOptInMethodTypes } from 'entities/Compliance'
import { CompliancePath } from 'pages/settings/pages/compliance/route/compliancePath'
import { TollFreeRoutesSteps } from 'pages/settings/pages/compliance/pages/tollFree/route/type'
import type { TollFreeStepsStore } from './tollFreeStepsStore'

const titleMap: Record<string, string> = {
  [TollFreeRoutesSteps.numbers]: 'Numbers',
  [TollFreeRoutesSteps.businessInfo]: 'Business info',
  [TollFreeRoutesSteps.contactInfo]: 'Contact info',
  [TollFreeRoutesSteps.optInMethod]: 'Opt-in method',
  [TollFreeRoutesSteps.useCase]: 'Use case',
  [TollFreeRoutesSteps.optInOnlineFormContent]: 'Opt-in consent: Online Form',
  [TollFreeRoutesSteps.optInPaperFormContent]: 'Opt-in consent: Paper Form',
  [TollFreeRoutesSteps.optInViaTextContent]: 'Opt-in consent: via Text',
  [TollFreeRoutesSteps.optInQrCodeContent]: 'Opt-in consent: QR Code',
  [TollFreeRoutesSteps.forbiddenMessageTopics]: 'Forbidden message topics',
}

export class TollFreeNavigationStore {
  private _profileId: number | null = null
  private _useCaseNumber: number | null = null
  private _currentStep = 1

  constructor(private _tollFreeStepsStore: TollFreeStepsStore) {
    makeAutoObservable(this)
  }

  init = (id: number) => {
    const { organizationTollFree } = this._tollFreeStepsStore.complianceLayerStore

    const tollFree = organizationTollFree?.getItem(id)

    this.setCurrentStep(tollFree?.continueStep || 1)

    if (!tollFree?.completed) {
      uiStore.changeRouteViaVue({
        method: 'replace',
        path: this.getCurrentUrl,
      })
    }
  }

  setProfileId = (id: number) => {
    this._profileId = id
  }

  setUseCaseNumber = (useCaseNumber?: number | string | null) => {
    this._useCaseNumber = useCaseNumber ? Number(useCaseNumber) : null
  }

  setCurrentStep = (step: number) => {
    this._currentStep = step
  }

  getStep = (direction: -1 | 1 | 0) => {
    const allSteps = Object.values(this.stepMapFiltered)
    const currentPageIndex = this._currentStep - 1

    return allSteps[currentPageIndex + direction]
  }

  getStepByPath = (tollFreeRoutes: TollFreeRoutesSteps) => {
    const allPaths = Object.entries(this.stepMapFiltered)
    const stepIndex = allPaths.findIndex((path) => {
      return path[1] === tollFreeRoutes
    })

    return stepIndex !== -1 ? stepIndex + 1 : 1
  }

  getStepUrlMap = (tollFreeRoutes: TollFreeRoutesSteps) => {
    return `${this.rootPath}/${tollFreeRoutes}`
  }

  get tollFreeStepsStore() {
    return this._tollFreeStepsStore
  }

  get profileId() {
    return this._profileId
  }

  get rootPath() {
    if (!this._profileId) {
      return CompliancePath.compliance.tollFree.steps
    }
    // debugger
    return this._useCaseNumber
      ? CompliancePath.compliance.tollFree.viewUseCaseSteps(this._profileId, this._useCaseNumber)
      : CompliancePath.compliance.tollFree.viewSteps(this._profileId)
  }

  get stepMap() {
    const allSteps = [
      TollFreeRoutesSteps.numbers,
      TollFreeRoutesSteps.businessInfo,
      TollFreeRoutesSteps.contactInfo,
      TollFreeRoutesSteps.useCase,
      TollFreeRoutesSteps.optInMethod,
      TollFreeRoutesSteps.optInOnlineFormContent,
      TollFreeRoutesSteps.optInPaperFormContent,
      TollFreeRoutesSteps.optInViaTextContent,
      TollFreeRoutesSteps.optInQrCodeContent,
      TollFreeRoutesSteps.forbiddenMessageTopics,
    ]

    const stepsToUse = this._tollFreeStepsStore.useCaseDetailsStore.rejectedReason
      ? allSteps.slice(1)
      : allSteps

    return Object.fromEntries(stepsToUse.map((step, index) => [index + 1, step]))
  }

  get stepMapFiltered() {
    const keys: TollFreeRoutesSteps[] = this.tollFreeStepsStore.optInMethodStore.optInMethods.map(
      (method) => {
        switch (method) {
          case EnumOptInMethodTypes.onlineForm:
            return TollFreeRoutesSteps.optInOnlineFormContent
          case EnumOptInMethodTypes.paperForm:
            return TollFreeRoutesSteps.optInPaperFormContent
          case EnumOptInMethodTypes.text:
            return TollFreeRoutesSteps.optInViaTextContent
          case EnumOptInMethodTypes.qrCode:
            return TollFreeRoutesSteps.optInQrCodeContent
          default:
            return null
        }
      }
    ) as TollFreeRoutesSteps[]

    return Object.entries(this.stepMap).reduce<Record<string, TollFreeRoutesSteps>>(
      (state, [step, page]) => {
        if (
          [
            TollFreeRoutesSteps.optInOnlineFormContent,
            TollFreeRoutesSteps.optInPaperFormContent,
            TollFreeRoutesSteps.optInViaTextContent,
            TollFreeRoutesSteps.optInQrCodeContent,
          ].includes(page) &&
          !keys.includes(page)
        ) {
          return state
        }

        state[step] = page

        return state
      },
      {}
    )
  }

  get getCurrentStep() {
    return this.getStep(0)
  }

  get getNextStep() {
    const step = this.getStep(1)

    if (!step) return this.getCurrentStep

    return step
  }

  get getLastStep() {
    const step = this.getStep(-1)

    if (!step) return this.getCurrentStep

    return step
  }

  get getCurrentUrl() {
    return `${this.rootPath}/${this.getCurrentStep}`
  }

  get getNextUrl() {
    return `${this.rootPath}/${this.getNextStep}`
  }

  get getLastUrl() {
    return `${this.rootPath}/${this.getLastStep}`
  }

  get allStepsLength() {
    const noMethodsFilled = this.tollFreeStepsStore.optInMethodStore.optInMethods.length === 0
    const stepLength = Object.keys(this.stepMapFiltered).length

    return noMethodsFilled ? stepLength + 1 : stepLength
  }

  get formStep() {
    return this._currentStep
  }

  get UIFormStep() {
    return this.formStep
  }

  decreaseStep = () => {
    uiStore.changeRoute({
      path: this.getLastUrl,
    })
  }

  getSettingsStep = (path: TollFreeRoutesSteps) => {
    return this.getStepByPath(path)
  }

  getProgressInfoTitle = (pathname: string) => {
    return titleMap[pathname] || ''
  }
}
